import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  LogOut, Users, Mail, CheckCircle2, BookOpenCheck, Trash2, RefreshCw,
} from "lucide-react";

const STATUS_OPTIONS = ["new", "contacted", "enrolled", "closed"];

export const AdminDashboard = () => {
  const { admin, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, i] = await Promise.all([api.get("/admin/stats"), api.get("/admin/inquiries")]);
      setStats(s.data);
      setInquiries(i.data);
    } catch (e) {
      toast.error("Could not load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      toast.success("Enquiry deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading font-bold text-xl text-[#0A3B32]">
            <span className="inline-flex w-8 h-8 rounded-lg bg-[#0A3B32] text-white items-center justify-center font-heading font-bold">
              B
            </span>
            BIOMAC · Admin
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500 hidden sm:inline">
              {admin?.email}
            </span>
            <Button variant="outline" size="sm" onClick={load} data-testid="admin-refresh-btn">
              <RefreshCw size={16} className="mr-1" /> Refresh
            </Button>
            <Button variant="destructive" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
              <LogOut size={16} className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-[#0A3B32]">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Manage student enquiries and track program performance.</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: "Total Enquiries", value: stats?.total_inquiries ?? 0, icon: Users, color: "text-[#0A3B32]" },
            { label: "New", value: stats?.new ?? 0, icon: Mail, color: "text-[#C85A40]" },
            { label: "Contacted", value: stats?.contacted ?? 0, icon: RefreshCw, color: "text-amber-600" },
            { label: "Enrolled", value: stats?.enrolled ?? 0, icon: CheckCircle2, color: "text-emerald-600" },
            { label: "Courses", value: stats?.courses ?? 0, icon: BookOpenCheck, color: "text-slate-700" },
          ].map((s) => (
            <Card key={s.label} data-testid={`admin-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
              <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-xs font-medium text-slate-500 uppercase tracking-wider">{s.label}</CardTitle>
                <s.icon size={16} className={s.color} />
              </CardHeader>
              <CardContent>
                <p className="font-heading text-3xl font-bold text-[#0A3B32]">{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card data-testid="admin-inquiries-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-[#0A3B32]">Enquiries</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : inquiries.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="admin-inquiries-empty">No enquiries yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="admin-inquiries-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Qualification</TableHead>
                      <TableHead>Course</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inquiries.map((i) => (
                      <TableRow key={i.id} data-testid={`inquiry-row-${i.id}`}>
                        <TableCell className="font-medium">{i.name}</TableCell>
                        <TableCell className="text-slate-600">{i.email}</TableCell>
                        <TableCell className="text-slate-600">{i.phone}</TableCell>
                        <TableCell className="text-slate-600">{i.qualification}</TableCell>
                        <TableCell className="text-slate-500 text-xs">{i.course_slug || "—"}</TableCell>
                        <TableCell>
                          <Select value={i.status} onValueChange={(v) => updateStatus(i.id, v)}>
                            <SelectTrigger className="w-[140px]" data-testid={`status-select-${i.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>
                                  {s}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-slate-500 text-xs">
                          {new Date(i.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => remove(i.id)}
                            data-testid={`delete-inquiry-${i.id}`}
                          >
                            <Trash2 size={16} className="text-red-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
